import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "./ComboModern.css";
import { useSettings } from "../context/SettingsContext";
const ComboMenuModal = ({ category, onSave, onClose, initialData = null }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selections, setSelections] = useState({});
  const [openGroupId, setOpenGroupId] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [specialInstruction, setSpecialInstruction] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  const [mobileView, setMobileView] = useState("list");
const isOptionPreselected = (option) =>
  option?.preselected == 1 || option?.preselected === true;
const { settings } = useSettings();
  // ✅ Preselect data when editing
  useEffect(() => {
    if (initialData && category) {
      const menuList = category.menu || category.menus || [];
      const idx = menuList.findIndex(
        (m) => m.menu?.id === initialData.menuId || m.menu?.id === initialData.id
      );
      if (idx >= 0) setSelectedIndex(idx);

      // rebuild selections from groupedModifiers if available
      if (initialData.groupedModifiers?.length) {
        const restored = {};
        initialData.groupedModifiers.forEach((g) => {
          const opts = {};
          g.options.forEach((o) => {
            opts[o.optionId] = o.quantity || 1;
          });
          restored[g.groupId] = opts;
        });
        setSelections(restored);
      }

      setQuantity(initialData.quantity || 1);
      setSpecialInstruction(initialData.specialInstruction || "");
      setMobileView(isMobile ? "details" : "list");
    }
  }, [initialData, category, isMobile]);

  // Handle resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  // if (!category) return null;

  const menuList = category.menu || category.menus || [];
  const selectedWrapper = selectedIndex != null ? menuList[selectedIndex] : null;
  const selectedMenu = selectedWrapper ? selectedWrapper.menu : null;
  const modifiers = selectedWrapper ? selectedWrapper.modifier || [] : [];


    // 🟢 Auto-preselect options when adding new (not editing)
useEffect(() => {
  // Run only if category and modifiers exist, and it's not edit mode
  if (!category || !selectedMenu || !modifiers.length || initialData) return;

  const preselects = {};
  let openGroup = null;

  modifiers.forEach((group) => {
    const preselectedOptions = group.options?.filter(
      (opt) => opt?.preselected === 1 || opt?.preselected === true
    );

    if (preselectedOptions.length > 0) {
      openGroup = group.id; // open this group
      preselectedOptions.forEach((opt) => {
        if (!preselects[group.id]) preselects[group.id] = {};
        preselects[group.id][opt.id] = 1; // quantity 1
      });
    }
  });

  if (Object.keys(preselects).length > 0) {
    setSelections((prev) => ({ ...prev, ...preselects }));
    if (openGroup) setOpenGroupId(openGroup);
  }
}, [category, modifiers, selectedMenu, initialData]);


  const toggleAccordion = (id) => {
    setOpenGroupId((prev) => (prev === id ? null : id));
  };

  const getTotalOptionQty = (groupId, selections) => {
  const opts = selections[groupId] || {};
  return Object.values(opts).reduce((sum, qty) => sum + qty, 0);
};


const handleToggleOption = (group, option) => {
  setSelections((prev) => {
    let groupSel = { ...(prev[group.id] || {}) };
    const isSelected = !!groupSel[option.id];

    const totalQty = Object.values(groupSel).reduce(
      (sum, qty) => sum + qty,
      0
    );

    // ❌ Block turning ON if max reached
    if (!isSelected && totalQty >= group.max_select) {
      toast.warn(`You cannot select more option(s) in "${group.name}"`);
      return prev;
    }

    if (isSelected) {
      delete groupSel[option.id];
    } else {
      if (group.max_select === 1 && group.min_select === 1) {
        groupSel = { [option.id]: 1 };
      } else {
        groupSel[option.id] = 1;
      }
    }

    return { ...prev, [group.id]: groupSel };
  });
};

  const incOpt = (gId, oId) =>
    setSelections((p) => ({
      ...p,
      [gId]: { ...(p[gId] || {}), [oId]: (p[gId]?.[oId] || 1) + 1 },
    }));

  const decOpt = (gId, oId) =>
    setSelections((p) => {
      const g = { ...(p[gId] || {}) };
      if (g[oId] > 1) g[oId] -= 1;
      else delete g[oId];
      return { ...p, [gId]: g };
    });

const validateSelections = () => {
  for (const g of modifiers) {
    const selectedQty = getTotalOptionQty(g.id, selections);

    if (g.type === 2 && selectedQty < g.max_select) {
      toast.error(`Please select ${g.max_select} option(s) in "${g.name}"`);
      return false;
    }

    if (g.type === 1 && g.min_select > 0 && selectedQty < g.min_select) {
      toast.error(`Please select at least ${g.min_select} option(s) in "${g.name}"`);
      return false;
    }
  }
  return true;
};


  const computeTotals = () => {
    const base = Number(selectedMenu?.price || 0);
    const mods = Object.entries(selections).reduce((acc, [gId, opts]) => {
      const group = modifiers.find((g) => g.id === gId);
      if (!group) return acc;
      Object.entries(opts).forEach(([oId, qty]) => {
        const opt = group.options.find((o) => o.id === oId);
        if (opt) acc += Number(opt.price || 0) * qty;
      });
      return acc;
    }, 0);
    const subtotal = base + mods;
    return { base, mods, subtotal, total: subtotal * quantity };
  };

const handleSave = () => {
  if (!selectedMenu) {
    toast.error("Please choose an item first");
    return;
  }
  if (!validateSelections()) return;

  // ------------ PRESELECTED OPTION HANDLING ------------
  let removedPreselectedNotes = [];

  modifiers.forEach((group) => {
    group.options.forEach((opt) => {
      const wasPreselected = isOptionPreselected(opt);
      const isNowSelected = selections[group.id]?.[opt.id] ? true : false;

      if (wasPreselected && !isNowSelected) {
        removedPreselectedNotes.push(`[No ${opt.name}]`);
      }
    });
  });

  // Current value
  let newInstruction = specialInstruction || "";

  // Remove old "[No ...]" when editing
  if (initialData && newInstruction) {
    newInstruction = newInstruction.replace(/\[No [^\]]+\]/g, "").trim();
  }

  // Add new notes if needed
  if (removedPreselectedNotes.length > 0) {
    newInstruction =
      (newInstruction ? newInstruction + " " : "") +
      removedPreselectedNotes.join(" ");
  }

  // Save back to state (for UI consistency)
  setSpecialInstruction(newInstruction);

  // ------------ BUILD GROUPED MODIFIERS ------------
  const grouped = modifiers
    .map((g) => {
      const opts = selections[g.id] || {};
      const arr = Object.entries(opts).map(([optId, qty]) => {
        const o = g.options.find((x) => x.id === optId);
        return {
          optionId: optId,
          name: o?.name || "",
          price: Number(o?.price || 0),
          quantity: qty,
        };
      });
      return { groupId: g.id, groupName: g.name, options: arr };
    })
    .filter((g) => g.options.length > 0);

  const totals = computeTotals();

  const menuData = {
    id: selectedMenu.id,
    menuId: selectedMenu.id,
    name: selectedMenu.name,
    image: selectedMenu.image,
    basePrice: Number(selectedMenu.price || 0),
    quantity,
    selectedModifiers: grouped.flatMap((g) => g.options),
    groupedModifiers: grouped,
    total: totals.total,
    specialInstruction: newInstruction.trim(), // <— final value
  };

  onSave(menuData);
};


  // 🧭 Mobile view handling
  const openDetails = (idx) => {
    setSelectedIndex(idx);
    setSelections({});
    setSpecialInstruction("");
    if (isMobile) setMobileView("details");
  };
  const goBackToList = () => setMobileView("list");

  return (
    <div className="cm-menu-modal-wrapper">
      <div className="cm-menu-modal">
        <header className="cmm-header">
          {isMobile && mobileView === "details" ? (
            <button className="cmm-back" onClick={goBackToList}>←</button>
          ) : (
            <h3 className="cmm-title">{category.name}</h3>
          )}
          <button className="cmm-close" onClick={onClose}>×</button>
        </header>

        <div className={`cmm-body ${isMobile ? "mobile" : ""}`}>
          {/* Menu list (hidden on mobile details view) */}
          {(!isMobile || mobileView === "list") && (
            <aside className="cmm-left">
              {menuList.map((m, idx) => (
                <button
                  key={idx}
                  className={`cmm-item ${selectedIndex === idx ? "active" : ""}`}
                  onClick={() => openDetails(idx)}
                >
                  <img src={m?.menu?.image} alt={m?.menu?.name} />
                  <div className="cmm-item-meta">
                    <div className="cmm-item-name"  style={{ color: settings?.cart_title_color || "#111827" }}>{m.menu.name}</div>
                    <div className="cmm-item-price">
                      {localStorage.getItem('currency')} {Number(m.menu.price || 0).toFixed(2)}
                    </div>
                  </div>
                </button>
              ))}
            </aside>
          )}

          {/* Right details panel */}
          {(!isMobile || mobileView === "details") && (
            <main className="cmm-right">
              {!selectedMenu && <div className="cmm-empty">Select an item</div>}

              {selectedMenu && (
                <>
                  <div className="cmm-selected">
                    <img src={selectedMenu.image} alt={selectedMenu.name} />
                    <div>
                      <div className="cmm-selected-name"  style={{ color: settings?.cart_title_color || "#111827" }}>{selectedMenu.name}</div>
                      <div className="cmm-selected-price"  style={{ color: settings?.cart_title_color || "#111827" }}>
                        {localStorage.getItem('currency')} {Number(selectedMenu.price || 0).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div className="cmm-modifiers">
                    {modifiers.map((g) => {
                      const sel = selections[g.id] || {};
                      const open = openGroupId === g.id;
                      return (
                        <div key={g.id} className="cmm-group">
                          <div
                            className="cmm-group-head"
                            onClick={() => toggleAccordion(g.id)}
                          >
                            <div>
                              {g.name}{" "}
                              {(() => {
  switch (g.type) {
    case 2:
      return `(Must Select ${g.max_select})`;
    case 1:
      return g.min_select > 0
        ? `(Select ${g.min_select}–${g.max_select})`
        : `(Upto ${g.max_select})`;
    case 4:
      return `(Preselected)`;
    case 3:
      return `(Optional Upto ${g.max_select})`;
    default:
      return `(Optional)`;
  }
})()}

                        {(() => {
  const selectedQty = getTotalOptionQty(g.id, selections);

  let isComplete = false;

  if (g.type === 2) {
    // Must select exact
    isComplete = selectedQty === g.max_select;
  } else if (g.type === 1) {
    if (g.min_select > 0) {
      isComplete =
        selectedQty >= g.min_select &&
        selectedQty <= g.max_select;
    } else {
      isComplete = selectedQty > 0;
    }
  } else if (g.type === 4) {
    isComplete = true;
  }

  return (
    isComplete && (
      <span className="cm-tick-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
    )
  );
})()}

                            </div>
                            <div className={`cmm-group-arrow ${open ? "open" : ""}`}>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </div>
                          </div>

                          {open && (
                            <div className="cmm-group-body">
                              {g.options.map((opt) => {
                                const isSel = !!sel[opt.id];
                                const qty = sel[opt.id] || 0;

                                const totalQty = getTotalOptionQty(g.id, selections);
                                const isMaxReached = totalQty >= g.max_select;

                                // disable + only if this option is selected AND group max reached
                                const disableQtyPlus = isSel && isMaxReached;

                                // disable toggle only when trying to turn ON
                                const disableToggle = !isSel && isMaxReached;

                                const optionPrice = Number(opt.price) || 0;
                                return (
                                  <div key={opt.id} className="cmm-option">
                                    <div className="cmm-option-left">
                                      <div className="cmm-option-name">{opt.name}</div>
                                     <div className="cmm-option-price">
                                      {optionPrice > 0 && (
                                        <>
                                          {localStorage.getItem('currency')} {optionPrice.toFixed(2)}
                                        </>
                                      )}
                                    </div>
                                    </div>
                                    <div className="cmm-option-actions">
                                      {isSel && (
                                        <div className="cmm-opt-qty">
                                          <button onClick={() => decOpt(g.id, opt.id)}>-</button>
                                          <span>{qty}</span>
                                          <button onClick={() => incOpt(g.id, opt.id)}   disabled={disableQtyPlus}
  style={{
    opacity: disableQtyPlus ? 0.5 : 1,
    cursor: disableQtyPlus ? "not-allowed" : "pointer",
  }}>+</button>
                                        </div>
                                      )}
                                      <label className="cmm-switch">
                                        <input
                                          type="checkbox"
                                          checked={isSel}
                                          onChange={() => handleToggleOption(g, opt)}
                                            disabled={disableToggle}
                                        />
                                        <span className="cmm-slider" />
                                      </label>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* 📝 Special Instructions */}
                  <div className="cmm-special-box">
                    <label className="cmm-special-label">Special Instructions (Optional)</label>
                    <textarea
                      value={specialInstruction}
                      onChange={(e) => setSpecialInstruction(e.target.value)}
                      placeholder="e.g. Make it extra spicy, no onions..."
                      className="cmm-special-textarea"
                    />
                  </div>

                  <div className="cmm-qty-row">
                    {selectedMenu?.is_quantity == 1 && (
                      <div className="cmm-qty-controls">
                        <button 
style={{
  background: settings?.cart_btn_color || "#000000ff",
   color: settings?.cart_btn_text_color || "#ffffff",
}}
onMouseEnter={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_hover || "#f59e0b";
}}
onMouseLeave={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_color || "#ffffff";
}} onClick={() => setQuantity(Math.max(1, quantity - 1))}>−</button>
                        <span>{quantity}</span>
                        <button 
style={{
  background: settings?.cart_btn_color || "#000000ff",
   color: settings?.cart_btn_text_color || "#ffffff",
}}
onMouseEnter={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_hover || "#f59e0b";
}}
onMouseLeave={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_color || "#ffffff";
}} onClick={() => setQuantity(quantity + 1)}>+</button>
                      </div>
                    )}
                    <div className="cmm-subtotal"  style={{ color: settings?.cart_title_color || "#111827" }}>
                      Total: {localStorage.getItem('currency')} {computeTotals().total.toFixed(2)}
                    </div>
                  </div>
                </>
              )}
            </main>
          )}
        </div>

        {/* Footer */}
        {(!isMobile || mobileView === "details") && selectedMenu && (
          <footer className="cmm-footer">
            {isMobile && (
              <button className="cmm-btn cancel" onClick={goBackToList}>
                Back
              </button>
            )}
            <button 
style={{
  background: settings?.cart_btn_color || "#000000ff",
   color: settings?.cart_btn_text_color || "#ffffff",
}}
onMouseEnter={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_hover || "#f59e0b";
}}
onMouseLeave={(e) => {
  e.currentTarget.style.background =
    settings?.cart_btn_color || "#ffffff";
}} className="cmm-btn save" onClick={handleSave}>
              Save • {localStorage.getItem('currency')} {computeTotals().total.toFixed(2)}
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

export default ComboMenuModal;
